import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Loader, Center, Text } from '@mantine/core';
import { useHotkeys, useMediaQuery } from '@mantine/hooks';
import { useAuthStore } from '../../../stores/auth';
import { useKianFlyoutStore } from '../../../stores/kianFlyout';
import { useKianPageContext } from '../../../providers/PageContextProvider';
import { useAssistantChat } from '../../../hooks/useAssistantChat';
import { countBusyReplies } from '../../../hooks/assistantPendingReplies';
import { useKianSession } from '../../../hooks/useKianSession';
import { useNavigatorOnline } from '../../../hooks/useNavigatorOnline';
import { createAssistantApi } from '../../../lib/api/domains/assistant';
import { apiRequest } from '../../../lib/api/http';
import { AssistantChat } from '../AssistantChat';
import { KianContextStack } from './KianContextStack';
import { KianFAB } from './KianFAB';
import { ServiceHubFAB } from '../../ServiceHub/ServiceHubFAB';
import { KianFlyoutHeader } from './KianFlyoutHeader';
import { KianFlyoutPanel } from './KianFlyoutPanel';
import { KianFlyoutTabs } from './KianFlyoutTabs';
import { KianRateLimitFooter } from './KianRateLimitFooter';
import { KianOnboardingTour } from './KianOnboardingTour';

const assistantApi = createAssistantApi(apiRequest);

/**
 * Globaler KIAN-Flyout: ServiceHub-FAB (Speed-Dial) + Panel (Portal / Mobile-Drawer), Kontext, Quick-Prompts.
 */
export function KianFlyoutRoot() {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isOnline = useNavigatorOnline();
  const [flyoutEnabled, setFlyoutEnabled] = useState(true);
  const [rateTick, setRateTick] = useState(0);

  const panelMode = useKianFlyoutStore((s) => s.panelMode);
  const togglePanel = useKianFlyoutStore((s) => s.togglePanel);
  const minimize = useKianFlyoutStore((s) => s.minimize);
  const setFullscreen = useKianFlyoutStore((s) => s.setFullscreen);
  const followPageContext = useKianFlyoutStore((s) => s.followPageContext);
  const setFollowPageContext = useKianFlyoutStore((s) => s.setFollowPageContext);
  const position = useKianFlyoutStore((s) => s.position);
  const size = useKianFlyoutStore((s) => s.size);
  const setPosition = useKianFlyoutStore((s) => s.setPosition);
  const setSize = useKianFlyoutStore((s) => s.setSize);
  const unread = useKianFlyoutStore((s) => s.unread);
  const clearUnread = useKianFlyoutStore((s) => s.clearUnread);
  const incrementUnread = useKianFlyoutStore((s) => s.incrementUnread);
  const setStreaming = useKianFlyoutStore((s) => s.setStreaming);
  const injectInput = useKianFlyoutStore((s) => s.injectInput);

  const { current, recent, pinned, pinEntity, unpinEntity, removeRecent } = useKianPageContext();
  const currentSessionId = useAssistantChat((s) => s.currentSessionId);
  // Gespräche, in denen KIAN gerade antwortet — auch die, die nicht zu sehen sind
  const busyReplies = useAssistantChat((s) => countBusyReplies(s.pendingReplies));
  const isSending = busyReplies > 0;
  const createSession = useAssistantChat((s) => s.createSession);

  const prevBusyRef = useRef(0);
  const headDragRef = useRef<{ startX: number; startRight: number } | null>(null);
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    startRight: number;
  } | null>(null);

  // Im KIAN-Fenster (/kian, /assistant) ist KIAN schon groß offen: Der Flyout würde dort mit
  // derselben Sitzungsverwaltung konkurrieren und die gewählte Unterhaltung umschalten.
  const { pathname } = useLocation();
  const onWorkspace = /^\/(kian|assistant)(\/|$)/.test(pathname);

  const open = panelMode !== 'closed' && panelMode !== 'minimized';
  const panelClosed = panelMode === 'closed' || panelMode === 'minimized' || onWorkspace;
  const isDocked = panelMode === 'docked';
  const isFullscreen = panelMode === 'fullscreen';
  const isFloating = panelMode === 'floating';

  useKianSession(isAuthenticated, flyoutEnabled, panelClosed, followPageContext, current);

  useHotkeys([
    ['mod+K', () => togglePanel()],
    ['mod+Shift+K', () => minimize()],
    [
      'Escape',
      () => {
        if (open) minimize();
      },
    ],
    [
      'mod+/',
      () => {
        if (open) injectInput('/');
      },
    ],
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void assistantApi
      .getKianConfig()
      .then((c) => setFlyoutEnabled(c.flyoutEnabled))
      .catch(() => setFlyoutEnabled(true));
  }, [isAuthenticated]);

  useEffect(() => {
    setStreaming(isSending);
    const closed = panelMode === 'closed' || panelMode === 'minimized';
    // Jede fertige Antwort zählt — auch wenn in einem anderen Gespräch noch eine läuft
    const finished = Math.max(0, prevBusyRef.current - busyReplies);
    for (let i = 0; i < finished && closed; i += 1) incrementUnread();
    if (finished > 0) {
      setRateTick((t) => t + 1);
    }
    prevBusyRef.current = busyReplies;
  }, [busyReplies, isSending, panelMode, incrementUnread, setStreaming]);

  useEffect(() => {
    if (open) clearUnread();
  }, [open, clearUnread]);

  const pageContextPayload = useMemo(
    () => ({
      primary: current ? { type: current.type, id: current.id, label: current.label, metadata: current.metadata } : undefined,
      recent: recent.map((r) => ({
        type: r.type,
        id: r.id,
        label: r.label,
        visitedAt: r.visitedAt,
      })),
      pinned: pinned.map((p) => ({
        type: p.type,
        id: p.id,
        label: p.label,
      })),
    }),
    [current, recent, pinned]
  );

  if (!isAuthenticated || !flyoutEnabled || onWorkspace) return null;

  const handlePinToggle = async () => {
    if (!currentSessionId) return;
    const previousFollow = followPageContext;
    setFollowPageContext(!followPageContext);
    try {
      await assistantApi.pinSession(currentSessionId, previousFollow);
    } catch {
      setFollowPageContext(previousFollow);
    }
  };

  const useDrawer = Boolean(isMobile);

  const kianDockedWidth = Math.min(Math.max(size.w, 320), 600);

  const panelPaperStyle: React.CSSProperties =
    isFullscreen && !useDrawer
      ? {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          borderRadius: 0,
        }
      : isDocked && !useDrawer
        ? {
            width: kianDockedWidth,
          }
        : {
            bottom: position.y,
            right: position.x,
            width: size.w,
            maxWidth: 'calc(100vw - 48px)',
            height: `clamp(360px, min(${size.h}px, calc(100vh - 96px)), calc(100vh - 24px))`,
            minHeight: 360,
          };

  const onHeadPointerDown = (e: React.PointerEvent<Element>) => {
    if (!isFloating || useDrawer) return;
    if ((e.target as HTMLElement).closest('button')) return;
    headDragRef.current = {
      startX: e.clientX,
      startRight: useKianFlyoutStore.getState().position.x,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onHeadPointerMove = (e: React.PointerEvent<Element>) => {
    if (!headDragRef.current) return;
    const dx = e.clientX - headDragRef.current.startX;
    const startR = headDragRef.current.startRight;
    const nextX = Math.max(12, Math.min(startR - dx, window.innerWidth - 100));
    const py = useKianFlyoutStore.getState().position.y;
    setPosition({ x: nextX, y: py });
  };

  const onHeadPointerUp = (e: React.PointerEvent<Element>) => {
    headDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onResDown = (e: React.PointerEvent<Element>) => {
    if ((!isFloating && !isDocked) || useDrawer) return;
    e.preventDefault();
    e.stopPropagation();
    const st = useKianFlyoutStore.getState();
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: st.size.w,
      startH: st.size.h,
      startRight: st.position.x,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onResMove = (e: React.PointerEvent<Element>) => {
    if (!resizeRef.current) return;
    const { startX, startY, startW, startH, startRight } = resizeRef.current;
    const dx = e.clientX - startX;
    if (isDocked) {
      const newW = Math.max(320, Math.min(600, startW - dx));
      setSize({ w: newW, h: startH });
      return;
    }
    const dy = e.clientY - startY;
    const newW = Math.max(360, Math.min(720, startW - dx));
    const newH = Math.max(480, Math.min(window.innerHeight * 0.9, startH - dy));
    const newRight = Math.max(12, Math.min(startRight - dx, window.innerWidth - 48));
    setSize({ w: newW, h: newH });
    const py = useKianFlyoutStore.getState().position.y;
    setPosition({ x: newRight, y: py });
  };

  const onResUp = (e: React.PointerEvent<Element>) => {
    resizeRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const headerAndContext = (
    <>
      <KianFlyoutHeader
        title={current ? `KIAN · ${current.label}` : 'KIAN · Freies Gespräch'}
        sessionId={currentSessionId}
        isFullscreen={isFullscreen}
        followPageContext={followPageContext}
        dragEnabled={isFloating && !useDrawer}
        dragHandleListeners={{
          onPointerDown: onHeadPointerDown,
          onPointerMove: onHeadPointerMove,
          onPointerUp: onHeadPointerUp,
          onPointerCancel: onHeadPointerUp,
        }}
        onFullscreen={() => setFullscreen(!isFullscreen)}
        onPinToggle={() => void handlePinToggle()}
        onAllChats={() => navigate(currentSessionId ? `/kian?session=${currentSessionId}` : '/kian')}
        onClose={() => minimize()}
      />

      <KianContextStack
        current={current}
        recent={recent}
        pinned={pinned}
        pinEntity={pinEntity}
        unpinEntity={unpinEntity}
        removeRecent={removeRecent}
      />

      <KianFlyoutTabs
        onNewSession={() => {
          void createSession({
            title: 'Freies Gespräch',
            forceNew: true,
          }).catch((err) => {
            console.error('Failed to create new Kian tab:', err);
          });
        }}
      />
    </>
  );

  const chatArea = (
    <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
      {currentSessionId ? (
        <AssistantChat
          key={currentSessionId}
          sessionId={currentSessionId}
          pageContext={pageContextPayload}
          streamReplies
          embedMode
        />
      ) : (
        <Center style={{ flex: 1 }}>
          <Stack align="center" gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Session wird geladen…
            </Text>
          </Stack>
        </Center>
      )}
    </Stack>
  );

  const inner =
    isFullscreen && !useDrawer ? (
      <>
        {headerAndContext}
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            background: 'var(--mantine-color-dark-8)',
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: 960,
              minHeight: 0,
              background: 'var(--mantine-color-body)',
              boxShadow: '0 0 0 1px var(--mantine-color-dark-5)',
            }}
          >
            {chatArea}
          </Box>
        </Box>
        <KianRateLimitFooter refreshToken={rateTick} />
      </>
    ) : (
      <>
        {headerAndContext}
        {chatArea}
        <KianRateLimitFooter refreshToken={rateTick} />
      </>
    );

  return (
    <>
      {open ? (
        <KianFAB onMinimize={() => minimize()} />
      ) : (
        <ServiceHubFAB
          kianUnread={unread}
          isStreaming={isSending}
          isOnline={isOnline}
          onKianOpen={() => togglePanel()}
        />
      )}

      <KianFlyoutPanel
        open={open}
        useDrawer={useDrawer}
        onDrawerClose={() => minimize()}
        isDocked={isDocked && !useDrawer}
        paperStyle={panelPaperStyle}
        transitionStyles={{}}
        showResizeHandle={(isFloating || isDocked) && !useDrawer}
        resizePointerHandlers={{
          onPointerDown: onResDown,
          onPointerMove: onResMove,
          onPointerUp: onResUp,
        }}
      >
        {inner}
      </KianFlyoutPanel>

      <KianOnboardingTour flyoutOpen={open} />
    </>
  );
}
